import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useMedia } from 'react-use'
import { compareAsc, addMonths, parse } from 'date-fns'

import { CREDIT_CARD_PAYMENT_METHOD, VOUCHER } from 'src/comps/PaymentForm/constants'
import { VARIANT_ALONE, VARIANT_FULLSCREEN } from 'client/components/Modal/constants'

import { createAliasVoucher } from 'client/contexts/Purchase/requests'
import { fetchVoucherRequestAction } from 'src/comps/Voucher/actions'
import { getEmail, getPayNow } from 'src/comps/Voucher/selectors'
import { getStatus as getStatusEmail } from 'src/effects/VerifyEmail/selectors'
import { typePaymentMethods } from 'client/types/checkout'
import { voucherPay } from 'src/comps/Voucher/requests'

import ButtonSimple from 'src/comps/ButtonSimple'
import EmbedHtml from 'src/comps/EmbedHtml'
import Feedback from 'client/components/FeedbackVoucher'
import Modal from 'client/components/Modal'
import PaymentForm from 'src/comps/PaymentForm'
import PayNow from 'src/comps/PayNow'
import Price from 'client/components/Price'
import VerifyEmailModal from 'src/effects/VerifyEmail'

import { useDevice } from 'src/hooks/useDevice'

import {
  CreditCardInfo,
  CreditCardNumberLabel,
  CreditCardNumberValue,
  FeedbackSection,
  PaidSection,
  PaymentDescription,
  PaymentDescriptionBold,
  PaymentInformationBase,
  PaymentInformationTitle,
  PaymentTotal,
  PendingPaymentTitle,
  PendingSection,
  PendingValue,
  Row,
  Transaction,
} from './styles'
import { breakpoint } from 'client/styles'
import messages from './messages'

const PaymentInformation = ({
  bookingHash,
  confirmedStatus,
  creditCard,
  currency,
  displayForm = true,
  hash,
  isCancellable,
  isCreditCard,
  isPayable,
  language,
  needToPay,
  paymentMethods,
  partialAmounts = {},
  pendingAmount,
  purchaseId,
  ticketsPaid = [],
  totalPaid,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { device } = useDevice()

  const isWide = useMedia(breakpoint.tablet, device !== 'mobile')

  const email = useSelector(getEmail)
  const statusResponseEmail = useSelector(getStatusEmail(hash))
  const payNow = useSelector(getPayNow)

  const [invalidCreditCard, setInvalidCreditCard] = useState(false)
  const [isButtonOn, setIsButtonOn] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [open, setOpen] = useState(false)
  const [paymentError, setPaymentError] = useState(false)
  const [secureCode, setSecureCode] = useState()
  const [isPartialPay, setPartialPay] = useState(false)

  function closeModal() {
    setIsButtonOn(true)
    setOpen(false)
  }

  function emailValidationMessage() {
    return isPartialPay ? messages.partialPay : messages.payNow
  }

  function onPaymentFail() {
    setPaymentError(true)
    setIsConfirming(false)
  }

  async function onPaymentSuccess(paymentMethod, paymentParams) {
    setIsConfirming(true)
    setPaymentError(false)

    try {
      if (paymentParams && paymentParams.paytpvToken) {
        await createAliasVoucher({ ...paymentParams, purchaseId })
      }

      const { data: confirmation } = await voucherPay({ purchaseId, language, hash })
      const challengeUrl = confirmation.challenge_url // eslint-disable-line camelcase

      if (challengeUrl) {
        window.location.href = challengeUrl
        return
      }

      const secureCodeRaw = confirmation.secure_html_raw // eslint-disable-line camelcase

      if (secureCodeRaw) {
        setSecureCode(secureCodeRaw)
        return
      }

      dispatch(fetchVoucherRequestAction({ purchaseId }))
    } catch {
      onPaymentFail()
    }
  }

  function shouldDisplayCreditCardPaid(tickets) {
    return (
      tickets.length === 0 ||
      tickets.some(ticket => ticket.paymentMethod === CREDIT_CARD_PAYMENT_METHOD)
    )
  }

  const checkCreditCardExpiration = expiryDate => {
    const currentDate = new Date()
    const expirationDate = parse(expiryDate, 'MMyy', new Date())
    const firstInvalidExpDate = addMonths(expirationDate, 1)

    const notExpired = compareAsc(firstInvalidExpDate, currentDate) > 0

    if (notExpired) {
      setOpen(true)
    } else {
      setInvalidCreditCard(true)
      setIsButtonOn(true)
    }
  }

  return (
    <PaymentInformationBase>
      <PaymentInformationTitle>
        <FormattedMessage {...messages.paymentInfo} />
      </PaymentInformationTitle>

      {creditCard && shouldDisplayCreditCardPaid(ticketsPaid) && (
        <CreditCardInfo>
          <CreditCardNumberLabel>
            <FormattedMessage {...messages.creditCardUsed} />
          </CreditCardNumberLabel>
          <CreditCardNumberValue>{creditCard?.number}</CreditCardNumberValue>
        </CreditCardInfo>
      )}

      {ticketsPaid.length > 0 && (
        <PaidSection separator={pendingAmount > 0 || ticketsPaid.length > 1}>
          {ticketsPaid.map(item => (
            <Transaction data-testid="paid-transaction" key={`ticket-${item.total}`}>
              <CreditCardInfo>
                <PaymentDescription>{intl.formatDate(item.paymentDate)}</PaymentDescription>
                <PaymentTotal>
                  <Price amount={item.total} currency={item.currency} />
                </PaymentTotal>
              </CreditCardInfo>
            </Transaction>
          ))}
        </PaidSection>
      )}

      {ticketsPaid.length > 1 && pendingAmount === 0 && (
        <Row>
          <CreditCardInfo>
            <PaymentDescriptionBold data-testid="total-payment">
              <FormattedMessage {...messages.total} />
            </PaymentDescriptionBold>
            <PaymentTotal>
              <Price amount={totalPaid} currency={currency} />
            </PaymentTotal>
          </CreditCardInfo>
        </Row>
      )}

      {pendingAmount > 0 && (
        <PendingSection>
          <Transaction data-testid="pending-transaction">
            <CreditCardInfo>
              <PaymentDescription>
                <PendingPaymentTitle>
                  <FormattedMessage {...messages.pendingToPay} />
                </PendingPaymentTitle>
              </PaymentDescription>

              <PendingValue>
                <Price amount={pendingAmount} currency={currency} />
              </PendingValue>
            </CreditCardInfo>
          </Transaction>

          {(isPayable || isCancellable) && (
            <>
              {isPayable &&
                isCreditCard &&
                confirmedStatus &&
                !(isButtonOn || needToPay) &&
                payNow && (
                  <Row>
                    <ButtonSimple
                      onClick={() => {
                        setPartialPay(false)
                        checkCreditCardExpiration(creditCard?.expirationDate)
                      }}
                      variant="primary"
                      dataTestId="pay-now"
                      text={intl.formatMessage(messages.payNow)}
                      fullWidth
                      disabled={isButtonOn}
                    />
                  </Row>
                )}

              {isPayable && confirmedStatus && partialAmounts.min && partialAmounts.max && (
                <Row>
                  <ButtonSimple
                    onClick={() => {
                      setPartialPay(true)
                      checkCreditCardExpiration(creditCard?.expirationDate)
                    }}
                    variant="tertiary"
                    dataTestId="pay-partial"
                    text={intl.formatMessage(messages.partialPay)}
                    fullWidth
                    disabled={isButtonOn}
                  />
                </Row>
              )}

              {confirmedStatus && displayForm && (
                <Row>
                  <ButtonSimple
                    onClick={() => setIsButtonOn(!isButtonOn)}
                    variant={needToPay ? 'primary' : 'tertiary'}
                    dataTestId="change-credit-card"
                    text={intl.formatMessage(messages.changeCreditCard)}
                    fullWidth
                  />
                </Row>
              )}

              {invalidCreditCard && (
                <FeedbackSection>
                  <Feedback
                    dataTestId="status-not-ok"
                    text={[intl.formatMessage(messages.warningCardExpiredAdd)]}
                    status="STATUS_NOT_OK"
                  />
                </FeedbackSection>
              )}
            </>
          )}

          {isButtonOn && displayForm && (
            <>
              <PaymentForm
                paymentMethods={paymentMethods}
                onSuccess={onPaymentSuccess}
                onError={onPaymentFail}
                error={paymentError}
                isConfirming={isConfirming}
                consumer={VOUCHER}
              />
              <EmbedHtml isBase64>{secureCode}</EmbedHtml>
            </>
          )}
        </PendingSection>
      )}

      <Modal
        handleClose={() => setOpen(false)}
        show={open}
        variant={isWide ? VARIANT_ALONE : VARIANT_FULLSCREEN}
        width="580px"
      >
        {email && statusResponseEmail === 'STATUS_OK' ? (
          <PayNow
            brand={creditCard?.brand}
            currency={currency}
            expiryDate={creditCard?.expirationDate}
            handleCloseModal={closeModal}
            holder={creditCard?.holder}
            number={creditCard?.number}
            pendingAmount={pendingAmount}
            purchaseId={purchaseId}
            isPartialPay={isPartialPay}
            partialAmounts={partialAmounts}
            bookingHash={bookingHash}
          />
        ) : (
          <VerifyEmailModal buttonText={intl.formatMessage(emailValidationMessage())} hash={hash} />
        )}
      </Modal>
    </PaymentInformationBase>
  )
}

PaymentInformation.propTypes = {
  bookingHash: PropTypes.string,
  confirmedStatus: PropTypes.bool,
  currency: PropTypes.string,
  displayForm: PropTypes.bool,
  hash: PropTypes.string,
  isCancellable: PropTypes.bool,
  isCreditCard: PropTypes.bool,
  isPayable: PropTypes.bool,
  language: PropTypes.string,
  needToPay: PropTypes.bool,
  partialAmounts: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  paymentMethods: typePaymentMethods,
  pendingAmount: PropTypes.number,
  purchaseId: PropTypes.string,
  ticketsPaid: PropTypes.arrayOf(Object),
  totalPaid: PropTypes.number,
  creditCard: PropTypes.shape({
    number: PropTypes.string,
    brand: PropTypes.string,
    holder: PropTypes.string,
    expirationDate: PropTypes.string,
  }),
}

export default PaymentInformation
